// src/pages/TestTemas.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useStudent } from "../context/StudentContext";
import { sameId } from "../utils/helpers";
import { API_URL } from "../config";
import Icon from "../components/Icon/Icon";
import { resolvePreguntasParaTestAttempt } from "../utils/selectPreguntasPorIntento";
import {
  canFinalizeTest,
  computeNota,
  countAciertos,
  resolveInFlightFinalize,
  shouldSkipTestSave,
} from "../utils/testTemasFinalizeGuard";
import { buildTestAttemptDetalle, getCorrectAnswerText } from "../utils/buildTestAttemptDetalle";
import {
  filterFailedReviewRows,
  buildTestReviewRows,
  shouldConfirmLeaveTestResults,
} from "../utils/testAttemptReview";
import { useBeforeUnloadWhen } from "../hooks/useBeforeUnloadWhen";
import CoursePestanasNav from "../components/CoursePestanasNav";
import {
  resolveTestExitAction,
  shouldShowExitConfirmModal,
  shouldShowFinishConfirmModal,
  shouldShowFinishTestButton,
} from "../utils/testTemasRunHeaderActions";
import { getMessage } from "../data/locales";
import { extractTopicTestHistory } from "../utils/extractTopicTestHistory";
import {
  accessStateFromRevokedBankError,
  buildPreTestInfoFromMemory,
  isRevokedTestAccess,
  loadQuestionBank,
  resolveStartEvaluationLabel,
  shouldApplyHistoryRefresh,
} from "../utils/testTemasStartFlow";
import { buildTestCourseReturnPath } from "../utils/testTemasCourseReturnPath";
import { prepareTestQuestions } from "../utils/prepareTestQuestions";
import { ensureExpandedKey, toggleExpandedKey } from "../utils/toggleExpandedKey";
import { scrollStudentContentToTop } from "../utils/scrollStudentContentToTop";
import { findLinkedTestTopic } from "../utils/findLinkedTestTopic";
import { appendTestAttemptToHistory } from "../utils/appendTestAttemptToHistory";
import "./TestTemas.css";

const shuffleArray = (array) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

function TestTemas({ embeddedQuery = null, onEmbeddedClose = null } = {}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEmbedded = embeddedQuery != null;
  const readQuery = (key) => {
    if (isEmbedded) {
      const value = embeddedQuery[key];
      return typeof value === "string" ? value : "";
    }
    return searchParams.get(key) || "";
  };
  const pestanaFromUrl = readQuery("pestana");
  const bloqueFromUrl = readQuery("bloque");
  const temaFromUrl = readQuery("tema");
  const courseIdFromUrl = readQuery("curso").trim();
  const isDirectEmbeddedLaunch = isEmbedded && temaFromUrl.trim().length > 0;
  const { activeCourse, courses, setActiveCourse, userProfile, loading: userProfileLoading } = useStudent();
  const returnCourseId = courseIdFromUrl || activeCourse?._id || "";
  const courseReturnPath = buildTestCourseReturnPath(
    pestanaFromUrl,
    bloqueFromUrl,
    temaFromUrl,
    returnCourseId,
  );
  const leaveToCourse = () => {
    if (typeof onEmbeddedClose === "function") {
      onEmbeddedClose();
      return;
    }
    navigate(courseReturnPath);
  };
  const leaveEmbeddedOrSelection = () => {
    if (typeof onEmbeddedClose === "function") {
      onEmbeddedClose();
      return;
    }
    setView("selection");
  };
  const [userId, setUserId] = useState(userProfile?._id ?? userProfile?.id ?? null);

  useEffect(() => {
    if (!courseIdFromUrl || activeCourse?._id === courseIdFromUrl) {
      return;
    }
    const requestedCourse = courses.find((course) => String(course?._id) === courseIdFromUrl);
    if (requestedCourse) {
      setActiveCourse(requestedCourse);
    }
  }, [activeCourse?._id, courseIdFromUrl, courses, setActiveCourse]);

  useEffect(() => {
    setUserId(userProfile?._id ?? userProfile?.id ?? null);
  }, [userProfile]);

  const [view, setView] = useState("loading");
  const [showPreTestModal, setShowPreTestModal] = useState(false);
  const [preTestInfo, setPreTestInfo] = useState(null);
  const [isStartingTest, setIsStartingTest] = useState(false);
  const [startTestError, setStartTestError] = useState(null);
  const MAX_INTENTOS = 10;
  
  const [estructura, setEstructura] = useState([]);
  const [cursoTitulo, setCursoTitulo] = useState("");
  const [userHistory, setUserHistory] = useState([]);
  const [testAccess, setTestAccess] = useState({ allowed: true });

  const [expandedBloques, setExpandedBloques] = useState([]);
  const [currentTestContext, setCurrentTestContext] = useState({ bloque: null, tema: null });
  
  const [preguntasTest, setPreguntasTest] = useState([]);
  const [testConfig, setTestConfig] = useState({ tiempoPorPregunta: 50, notaMinima: 5 });
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [respuestasUsuario, setRespuestasUsuario] = useState({}); 
  const [preguntasMarcadasRevision, setPreguntasMarcadasRevision] = useState({});
  const [testFinalizado, setTestFinalizado] = useState(false);
  
  const [tiempoRestante, setTiempoRestante] = useState(0);
  const [tiempoInicio, setTiempoInicio] = useState(null);
  const rawPreguntasRef = useRef([]);
  const [errorMsg, setErrorMsg] = useState(null);
  const [retryTick, setRetryTick] = useState(0);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);
  const [showLeaveResultsConfirm, setShowLeaveResultsConfirm] = useState(false);
  const [historyReviewEntry, setHistoryReviewEntry] = useState(null);
  const [resultAciertos, setResultAciertos] = useState(0);
  const [resultNotaFinal, setResultNotaFinal] = useState(0);
  const [resultPassed, setResultPassed] = useState(false);

  const timeoutRef = useRef(null);
  const finalizadoRef = useRef(false);
  const testSaveStartedRef = useRef(false);
  const testSavedRef = useRef(false);
  const skipSaveRef = useRef(false);
  const loadedStructureCourseIdRef = useRef(null);
  const lastRetryTickRef = useRef(retryTick);
  const pendingPreSelectRef = useRef(null);
  const deepLinkOpenedRef = useRef(false);
  const deepLinkKeyRef = useRef("");
  const startAbortRef = useRef(null);
  const userHistoryRef = useRef(userHistory);
  const historyRefreshGenerationRef = useRef(0);
  const historyReviewBodyRef = useRef(null);

  const scrollHistoryReview = useCallback((edge) => {
    const reviewBody = historyReviewBodyRef.current;
    if (!reviewBody) return;
    reviewBody.scrollTop = edge === "end" ? reviewBody.scrollHeight : 0;
    reviewBody.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    userHistoryRef.current = userHistory;
  }, [userHistory]);

  const pendingResultsReview =
    testFinalizado &&
    view === "test" &&
    shouldConfirmLeaveTestResults(preguntasTest, respuestasUsuario);

  useBeforeUnloadWhen(pendingResultsReview);

  const requestLeaveTestResults = () => {
    if (!shouldConfirmLeaveTestResults(preguntasTest, respuestasUsuario)) {
      leaveToCourse();
      return;
    }
    setShowLeaveResultsConfirm(true);
  };

  const handleRunExitClick = () => {
    if (resolveTestExitAction(testFinalizado) === "leaveResults") {
      requestLeaveTestResults();
      return;
    }
    setShowExitConfirm(true);
  };

  const handleRunFinishClick = () => {
    if (testFinalizado) {
      return;
    }
    setShowFinishConfirm(true);
  };

  // Initial load: full-page spinner only when structure for this course is missing.
  // When userId arrives later, refresh history silently (no UI flash).
  useEffect(() => {
    if (!activeCourse?._id) {
      setView("error");
      setErrorMsg("No hay un curso activo seleccionado.");
      return;
    }

    let cancelled = false;
    const courseId = activeCourse._id;
    const forceStructureReload =
      loadedStructureCourseIdRef.current !== courseId ||
      lastRetryTickRef.current !== retryTick;
    lastRetryTickRef.current = retryTick;

    if (forceStructureReload) {
      deepLinkOpenedRef.current = false;
    }

    const run = async () => {
      if (forceStructureReload) {
        setView("loading");
      }
      try {
        if (forceStructureReload) {
          const res = await fetch(`${API_URL}/api/cursos/${courseId}/tests/estructura`, {
            credentials: "include",
          });
          if (!res.ok) {
            throw new Error("Error al cargar la estructura.");
          }
          const data = await res.json();
          if (cancelled) {
            return;
          }
          setEstructura(data.estructura || []);
          setCursoTitulo(data.titulo);
          setTestAccess(data.accesoTests || { allowed: true });
          loadedStructureCourseIdRef.current = courseId;
        }

        if (userId) {
          const requestGeneration = historyRefreshGenerationRef.current + 1;
          historyRefreshGenerationRef.current = requestGeneration;
          const historyRes = await fetch(`${API_URL}/api/usuarios/${userId}/tests`, {
            credentials: "include",
          });
          if (historyRes.ok && !cancelled && shouldApplyHistoryRefresh(requestGeneration, historyRefreshGenerationRef.current)) {
            const historyData = await historyRes.json();
            const nextHistory = Array.isArray(historyData) ? historyData : [];
            if (!cancelled) {
              setUserHistory((current) => (
                shouldApplyHistoryRefresh(requestGeneration, historyRefreshGenerationRef.current)
                  ? nextHistory
                  : current
              ));
            }
          }
        }

        if (forceStructureReload && !cancelled) {
          setView("selection");
        }
      } catch (err) {
        if (!cancelled) {
          loadedStructureCourseIdRef.current = null;
          setErrorMsg(err instanceof Error ? err.message : "Error al cargar los datos de evaluación.");
          setView("error");
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [activeCourse, userId, retryTick]);

  useEffect(() => {
    if (view !== "selection" || estructura.length === 0) {
      return;
    }
    const targetBlock = bloqueFromUrl.trim() || pestanaFromUrl.trim();
    if (targetBlock.length === 0) {
      return;
    }
    const match = estructura.find((bloque) => String(bloque.nombre) === targetBlock);
    if (match) {
      setExpandedBloques((current) => ensureExpandedKey(current, match.nombre));
    }
  }, [view, estructura, bloqueFromUrl, pestanaFromUrl]);

  useEffect(() => {
    const deepLinkKey = `${bloqueFromUrl}|${pestanaFromUrl}|${temaFromUrl}`;
    if (deepLinkKeyRef.current !== deepLinkKey) {
      deepLinkKeyRef.current = deepLinkKey;
      deepLinkOpenedRef.current = false;
    }
  }, [bloqueFromUrl, pestanaFromUrl, temaFromUrl]);

  // Deep links previously center-scrolled a topic row inside `.side-content`,
  // which hid the course title. Keep the student pane pinned to the top after layout.
  useEffect(() => {
    if (view !== "selection") {
      return;
    }
    scrollStudentContentToTop();
    const frame = requestAnimationFrame(() => {
      scrollStudentContentToTop();
    });
    const delayed = window.setTimeout(() => {
      scrollStudentContentToTop();
    }, 120);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(delayed);
    };
  }, [view, bloqueFromUrl, pestanaFromUrl, temaFromUrl]);

  const getBlockProgress = (blockName, blockTopics) => {
    if (!userHistory || userHistory.length === 0) return { master: 0, total: blockTopics.length };
    const courseHistory = userHistory.find(c => sameId(c.cursoId, activeCourse?._id));
    if (!courseHistory || !courseHistory.bloques) return { master: 0, total: blockTopics.length };
    const userBlock = courseHistory.bloques.find(b => b.nombreBloque === blockName);
    if (!userBlock || !Array.isArray(userBlock.temas)) return { master: 0, total: blockTopics.length };

    let masterCount = 0;
    blockTopics.forEach(topic => {
        const userTopic = userBlock.temas.find(t => t.nombreTema === topic.nombre);
        if (userTopic?.historial?.some(h => h.estado === "dominado")) masterCount++;
    });
    return { master: masterCount, total: blockTopics.length };
  };

  const isTopicMastered = (blockName, topicName) => {
    if (!userHistory || userHistory.length === 0) return false;
    const courseHistory = userHistory.find(c => sameId(c.cursoId, activeCourse?._id));
    if (!courseHistory || !courseHistory.bloques) return false;
    const userBlock = courseHistory.bloques.find(b => String(b.nombreBloque) === String(blockName));
    if (!userBlock) return false;
    const userTopic = userBlock.temas?.find(t => String(t.nombreTema) === String(topicName));
    if (!userTopic || !userTopic.historial) return false;
    return userTopic.historial.some(h => h.estado === "dominado");
  };

  const handleToggleBloque = (bloqueNombre) => {
    setExpandedBloques((current) => toggleExpandedKey(current, bloqueNombre));
  };

  const closePreTestModal = useCallback(() => {
    if (isStartingTest) {
      startAbortRef.current?.abort();
    }
    setShowPreTestModal(false);
    setStartTestError(null);
    setIsStartingTest(false);
    setHistoryReviewEntry(null);
  }, [isStartingTest]);

  const openPreTestModal = useCallback(
    (tema, bloqueNombre) => {
      setPreTestInfo(
        buildPreTestInfoFromMemory({
          tema,
          bloqueNombre,
          userHistory: userHistoryRef.current,
          courseId: activeCourse?._id,
        }),
      );
      setStartTestError(null);
      setIsStartingTest(false);
      setShowPreTestModal(true);

      if (!userId || !activeCourse?._id) {
        return;
      }

      const courseId = activeCourse._id;
      const temaNombre = tema.nombre;
      const requestGeneration = historyRefreshGenerationRef.current + 1;
      historyRefreshGenerationRef.current = requestGeneration;
      (async () => {
        try {
          const res = await fetch(`${API_URL}/api/usuarios/${userId}/tests`, {
            credentials: "include",
          });
          if (!res.ok || !shouldApplyHistoryRefresh(requestGeneration, historyRefreshGenerationRef.current)) {
            return;
          }
          const fullHistory = await res.json();
          if (!shouldApplyHistoryRefresh(requestGeneration, historyRefreshGenerationRef.current)) {
            return;
          }
          const nextHistory = Array.isArray(fullHistory) ? fullHistory : [];
          setUserHistory((current) => (
            shouldApplyHistoryRefresh(requestGeneration, historyRefreshGenerationRef.current)
              ? nextHistory
              : current
          ));
          const refreshed = extractTopicTestHistory(
            nextHistory,
            courseId,
            bloqueNombre,
            temaNombre,
          );
          setPreTestInfo((prev) => {
            if (
              !shouldApplyHistoryRefresh(requestGeneration, historyRefreshGenerationRef.current) ||
              !prev ||
              String(prev.bloque) !== String(bloqueNombre) ||
              String(prev.temaObj?.nombre) !== String(temaNombre)
            ) {
              return prev;
            }
            return {
              ...prev,
              historial: refreshed.historial,
              intentos: refreshed.intentos,
            };
          });
        } catch {
          // Background refresh must not block starting a test.
        }
      })();
    },
    [activeCourse?._id, userId],
  );

  const handlePreSelectTema = useCallback(
    (tema, bloqueNombre) => {
      if (!testAccess.allowed) {
        return;
      }
      if (!userId) {
        if (userProfileLoading) {
          pendingPreSelectRef.current = { tema, bloqueNombre };
          return;
        }
        setErrorMsg("No se pudo cargar tu perfil. Recarga la página.");
        setView("error");
        return;
      }
      openPreTestModal(tema, bloqueNombre);
    },
    [openPreTestModal, testAccess.allowed, userId, userProfileLoading],
  );

  useEffect(() => {
    if (!userId || !pendingPreSelectRef.current) {
      return;
    }
    const pending = pendingPreSelectRef.current;
    pendingPreSelectRef.current = null;
    openPreTestModal(pending.tema, pending.bloqueNombre);
  }, [userId, openPreTestModal]);

  useEffect(() => {
    if (view !== "selection" || estructura.length === 0 || deepLinkOpenedRef.current) {
      return;
    }
    const temaName = temaFromUrl.trim();
    if (temaName.length === 0) {
      return;
    }
    const targetBlock = bloqueFromUrl.trim() || pestanaFromUrl.trim();
    const linkedTest = findLinkedTestTopic(estructura, temaName, targetBlock);
    if (linkedTest) {
      const { block: bloque, topic: tema } = linkedTest;
      deepLinkOpenedRef.current = true;
      setExpandedBloques((current) => ensureExpandedKey(current, bloque.nombre));
      handlePreSelectTema(tema, bloque.nombre);
    }
  }, [
    view,
    estructura,
    temaFromUrl,
    bloqueFromUrl,
    pestanaFromUrl,
    handlePreSelectTema,
  ]);

  const handleSelectTema = async (tema, bloqueNombre) => {
    if (isStartingTest) {
      return;
    }
    setIsStartingTest(true);
    setStartTestError(null);
    finalizadoRef.current = false;
    testSaveStartedRef.current = false;
    testSavedRef.current = false;
    skipSaveRef.current = false;
    setCurrentTestContext({ bloque: bloqueNombre, tema: tema.nombre });
    const abortController = new AbortController();
    startAbortRef.current = abortController;

    try {
      let config = { tiempoPorPregunta: tema.tiempoPorPregunta ?? 50, notaMinima: tema.notaMinima ?? 5.0 };
      const query = new URLSearchParams({ bloque: bloqueNombre, tema: tema.nombre });
      const data = await loadQuestionBank(
        `${API_URL}/api/cursos/${activeCourse._id}/tests/banco?${query}`,
        abortController.signal,
      );
      const preguntasRaw = data.preguntas;
      const totalEnBanco = data.totalPreguntasEnBanco ?? null;
      if (data.tiempoPorPregunta != null) config.tiempoPorPregunta = data.tiempoPorPregunta;
      if (data.notaMinima != null) config.notaMinima = data.notaMinima;
      if (data.preguntasPorIntento != null) config.preguntasPorIntento = data.preguntasPorIntento;
      if (!preguntasRaw || preguntasRaw.length === 0) throw new Error("No hay preguntas disponibles.");
      
      setTestConfig(config);
      const preguntasParaIntento = resolvePreguntasParaTestAttempt(
        preguntasRaw,
        config.preguntasPorIntento,
        totalEnBanco
      );
      rawPreguntasRef.current = preguntasParaIntento;

      const preguntasProcesadas = procesarPreguntasParaTest(preguntasParaIntento);
      
      setPreguntasTest(preguntasProcesadas);
      const tiempoLimite = preguntasProcesadas.length * config.tiempoPorPregunta;
      setTiempoRestante(tiempoLimite); 
      setTiempoInicio(Date.now()); 
      setCurrentQuestionIndex(0); 
      setRespuestasUsuario({});
      setPreguntasMarcadasRevision({});
      setTestFinalizado(false);
      setResultAciertos(0);
      setResultNotaFinal(0);
      setResultPassed(false);
      setShowPreTestModal(false);
      setIsStartingTest(false);
      setView("test");
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        setIsStartingTest(false);
        return;
      }
      if (isRevokedTestAccess(err)) {
        setTestAccess(accessStateFromRevokedBankError(err));
        setShowPreTestModal(false);
        setStartTestError(null);
        setIsStartingTest(false);
        return;
      }
      setStartTestError(
        err instanceof Error ? err.message : getMessage("tests.startError"),
      );
      setIsStartingTest(false);
    } finally {
      if (startAbortRef.current === abortController) {
        startAbortRef.current = null;
      }
    }
  };

  const procesarPreguntasParaTest = (preguntasOriginales) => {
    return prepareTestQuestions(preguntasOriginales, shuffleArray);
  };

  const handleRespuesta = (indexRespuesta) => {
    if (testFinalizado) return;
    setRespuestasUsuario((prev) => {
      if (prev[currentQuestionIndex] === indexRespuesta) {
        const next = { ...prev };
        delete next[currentQuestionIndex];
        return next;
      }
      return { ...prev, [currentQuestionIndex]: indexRespuesta };
    });
  };

  const togglePreguntaMarcadaRevision = (questionIndex) => {
    if (testFinalizado) return;
    setPreguntasMarcadasRevision((prev) => {
      const next = { ...prev };
      if (next[questionIndex]) {
        delete next[questionIndex];
      } else {
        next[questionIndex] = true;
      }
      return next;
    });
  };

  const guardarResultadoEnBackend = useCallback(async (nota, duracionSegundos, estado, respuestasDetalle) => {
    if (!userId || !currentTestContext.bloque || !currentTestContext.tema) return;
    const idTest = `${currentTestContext.bloque}-${currentTestContext.tema}-${Date.now()}`;
    const payload = {
      bloque: currentTestContext.bloque,
      tema: currentTestContext.tema,
      idTest,
      nota,
      tiempo: duracionSegundos,
      cursoId: activeCourse._id,
      estado,
      cursoTitulo: cursoTitulo || activeCourse?.titulo || "",
      respuestasDetalle: Array.isArray(respuestasDetalle) ? respuestasDetalle : [],
    };
    const res = await fetch(`${API_URL}/api/usuarios/${userId}/tests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include'
    });
    if (!res.ok) throw new Error(`Error del servidor: ${res.status}`);
    const savedAttempt = await res.json();
    historyRefreshGenerationRef.current += 1;
    setUserHistory((current) => appendTestAttemptToHistory(current, savedAttempt));
  }, [userId, currentTestContext, activeCourse?._id, activeCourse?.titulo, cursoTitulo]);

  const cancelPendingAutoFinalize = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    skipSaveRef.current = true;
    finalizadoRef.current = true;
  }, []);

  const finalizarTest = useCallback(async () => {
    setShowFinishConfirm(false);
    setShowExitConfirm(false);

    if (shouldSkipTestSave(skipSaveRef.current)) {
      return false;
    }

    const inFlight = resolveInFlightFinalize(
      testSaveStartedRef.current,
      testSavedRef.current
    );
    if (inFlight !== null) {
      return inFlight;
    }
    testSaveStartedRef.current = true;

    if (!canFinalizeTest(preguntasTest.length)) {
      testSaveStartedRef.current = false;
      setErrorMsg("No hay preguntas en este test.");
      setView("error");
      return false;
    }

    let duracionSegundos = 0;
    if (tiempoInicio) duracionSegundos = Math.floor((Date.now() - tiempoInicio) / 1000);

    const aciertos = countAciertos(preguntasTest, respuestasUsuario);
    const nota = computeNota(aciertos, preguntasTest.length);
    const estado = nota >= testConfig.notaMinima ? 'dominado' : 'debil';

    const respuestasDetalle = buildTestAttemptDetalle(preguntasTest, respuestasUsuario);

    try {
      await guardarResultadoEnBackend(Number(nota.toFixed(2)), duracionSegundos, estado, respuestasDetalle);
      setResultAciertos(aciertos);
      setResultNotaFinal(nota);
      setResultPassed(nota >= testConfig.notaMinima);
      setTestFinalizado(true);
      testSavedRef.current = true;
      return true;
    } catch (error) {
      testSaveStartedRef.current = false;
      testSavedRef.current = false;
      console.error("Fallo al guardar resultado:", error);
      setErrorMsg(error?.message || "No se pudo guardar el resultado.");
      setView("error");
      return false;
    }
  }, [tiempoInicio, preguntasTest, respuestasUsuario, guardarResultadoEnBackend, testConfig]);

  useEffect(() => {
    let intervalo;
    if (view === "test" && !testFinalizado && tiempoRestante > 0) {
      intervalo = setInterval(() => {
        setTiempoRestante((prev) => {
          if (prev <= 1 && !finalizadoRef.current && !skipSaveRef.current) {
            finalizadoRef.current = true;
            timeoutRef.current = setTimeout(() => finalizarTest(), 0);
            return 0;
          }
          return prev <= 1 ? 0 : prev - 1;
        });
      }, 1000);
    }
    return () => {
      clearInterval(intervalo);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [view, testFinalizado, tiempoRestante, finalizarTest]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60); const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const reiniciarTest = async () => {
    if (!activeCourse?._id || !currentTestContext.bloque || !currentTestContext.tema) {
      return;
    }
    finalizadoRef.current = false;
    testSaveStartedRef.current = false;
    testSavedRef.current = false;
    skipSaveRef.current = false;
    setView("loading");
    try {
      const query = new URLSearchParams({
        bloque: currentTestContext.bloque,
        tema: currentTestContext.tema,
      });
      const data = await loadQuestionBank(
        `${API_URL}/api/cursos/${activeCourse._id}/tests/banco?${query}`,
      );
      const preguntasRaw = data.preguntas;
      if (!preguntasRaw || preguntasRaw.length === 0) {
        throw new Error("No hay preguntas disponibles.");
      }
      const preguntasParaIntento = resolvePreguntasParaTestAttempt(
        preguntasRaw,
        testConfig.preguntasPorIntento,
        data.totalPreguntasEnBanco ?? null
      );
      rawPreguntasRef.current = preguntasParaIntento;
      const preguntasProcesadas = procesarPreguntasParaTest(preguntasParaIntento);
      setPreguntasTest(preguntasProcesadas);
      const tiempoLimite = preguntasProcesadas.length * testConfig.tiempoPorPregunta;
      setTiempoRestante(tiempoLimite);
      setTiempoInicio(Date.now());
      setCurrentQuestionIndex(0);
      setRespuestasUsuario({});
      setPreguntasMarcadasRevision({});
      setTestFinalizado(false);
      setResultAciertos(0);
      setResultNotaFinal(0);
      setResultPassed(false);
      setView("test");
    } catch (err) {
      if (isRevokedTestAccess(err)) {
        setTestAccess(accessStateFromRevokedBankError(err));
        setShowPreTestModal(false);
        setStartTestError(null);
        setView("selection");
        return;
      }
      setErrorMsg(err instanceof Error ? err.message : "Error al reiniciar el test.");
      setView("error");
    }
  };

  // --- RENDERS ---

  if (view === "loading") {
    return (
        <div className="cds-test-root dark-theme">
            <div className="cds-test-loading">
                <div className="cds-test-spinner"></div>
                <p>Cargando datos de evaluación...</p>
            </div>
        </div>
    );
  }

  if (view === "error") {
    return (
        <div className="cds-test-root dark-theme">
            <div className="cds-test-error">
                <Icon name="xCircle" className="cds-test-icon cds-test-icon-error"/>
                <p>{errorMsg}</p>
                {typeof onEmbeddedClose === "function" ? (
                  <button
                    type="button"
                    className="cds-test-btn-primary"
                    style={{ display: "inline-block", textAlign: "center", cursor: "pointer" }}
                    onClick={onEmbeddedClose}
                  >
                    ← Volver al Campus
                  </button>
                ) : (
                  <Link to="/usuario/campus" className="cds-test-btn-primary" style={{ display: 'inline-block', textDecoration: 'none', textAlign: 'center', cursor: 'pointer' }}>← Volver al Campus</Link>
                )}
                <button
                  className="cds-test-btn-outline"
                  style={{ marginTop: 8 }}
                  onClick={() => {
                    setErrorMsg(null);
                    setRetryTick((v) => v + 1);
                    setView("loading");
                  }}
                  aria-label="Reintentar cargar"
                >
                  Reintentar
                </button>
            </div>
        </div>
    );
  }

  if (view === "selection") {
    const selectionProgress = estructura.reduce(
      (acc, bloque) => {
        const progress = getBlockProgress(bloque.nombre, bloque.temas || []);
        return {
          master: acc.master + progress.master,
          total: acc.total + progress.total,
        };
      },
      { master: 0, total: 0 },
    );

    return (
      <div className="cds-test-root dark-theme">
        <header className="cds-test-header">
          {typeof onEmbeddedClose === "function" ? (
            <button
              type="button"
              className="cds-test-back-link"
              aria-label={getMessage("campus.backToCampus")}
              onClick={onEmbeddedClose}
            >
              {getMessage("campus.backToCampus")}
            </button>
          ) : (
            <Link to="/usuario/campus" className="cds-test-back-link" aria-label={getMessage("campus.backToCampus")}>
              {getMessage("campus.backToCampus")}
            </Link>
          )}
          <h1>{cursoTitulo}</h1>
          <p>{getMessage("campus.courseContentSubtitle")}</p>
          <CoursePestanasNav />
        </header>

        {!isDirectEmbeddedLaunch && estructura.length > 0 ? (
          <div className="cds-test-selection-summary" aria-live="polite">
            <span className="cds-test-selection-summary-count">
              {getMessage("blocks.completedCount", {
                completed: selectionProgress.master,
                total: selectionProgress.total,
              })}
            </span>
          </div>
        ) : null}

        {!testAccess.allowed ? (
          <div className="cds-test-access-lock" role="status">
            <span className="cds-test-access-lock-icon" aria-hidden="true">🔒</span>
            <div>
              <strong>Tests bloqueados</strong>
              <p>{testAccess.message || "Completa todo el contenido del curso antes de acceder a los tests."}</p>
              {Number.isFinite(testAccess.totalContent) && testAccess.totalContent > 0 ? (
                <p className="cds-test-access-lock-progress">
                  Progreso del contenido: {testAccess.completedContent || 0} de {testAccess.totalContent}
                  {" "}({testAccess.percentage || 0}%)
                </p>
              ) : null}
            </div>
          </div>
        ) : null}

        {!isDirectEmbeddedLaunch && estructura.length === 0 ? (
          <div className="cds-test-empty-catalog" role="status">
            <Icon name="info" className="cds-test-icon" />
            <p>No hay tests configurados para este curso todavía.</p>
            <p className="cds-test-empty-hint">Si deberían aparecer, contacta con tu instructor o recarga la página.</p>
            <button
              type="button"
              className="cds-test-btn-outline"
              onClick={() => {
                setRetryTick((v) => v + 1);
                setView("loading");
              }}
            >
              Reintentar
            </button>
          </div>
        ) : !isDirectEmbeddedLaunch ? (
        <div className="cds-test-grid">
            {estructura.map((bloque, idx) => {
                const isExpanded = expandedBloques.includes(bloque.nombre);
                const progress = getBlockProgress(bloque.nombre, bloque.temas);
                const percent = progress.total > 0 ? (progress.master / progress.total) * 100 : 0;
                
                return (
                    <div key={idx} className={`cds-test-card ${isExpanded ? 'is-expanded' : ''}`}>
                        <button className="cds-test-card-trigger" onClick={() => handleToggleBloque(bloque.nombre)}>
                            <div className="cds-test-card-info">
                                <h2>{bloque.nombre}</h2>
                                <div className="progress-container-mini">
                                    <div className="cds-test-progress-track">
                                        <div className="cds-test-progress-fill" style={{width: `${percent}%`}}></div>
                                    </div>
                                    <span className="cds-test-meta-text">{progress.master}/{progress.total} dominados</span>
                                </div>
                            </div>
                            <div className="cds-test-icon-box">
                                <Icon name="chevronDown" className={`cds-test-icon cds-test-chevron ${isExpanded ? 'rotated' : ''}`} />
                            </div>
                        </button>
                        
                        <div className={`cds-test-collapse ${isExpanded ? 'open' : ''}`}>
                            <div className="cds-test-topic-list">
                                {bloque.temas.length > 0 ? (
                                    bloque.temas.map((tema, tIdx) => {
                                        const dominado = isTopicMastered(bloque.nombre, tema.nombre);
                                        return (
                                        <div
                                          key={tIdx}
                                          id={`test-topic-row-${bloque.nombre}-${encodeURIComponent(tema.nombre)}`}
                                          className={`cds-test-topic-row${tema.nombre === temaFromUrl ? " is-nav-focused" : ""}`}
                                        >
                                            <div className="cds-test-topic-left">
                                                {dominado ? <Icon name="checkCircle" className="cds-test-icon cds-test-status-mastered" /> : <div className="cds-test-status-dot"></div>}
                                                <span className="cds-test-topic-name">{tema.nombre}</span>
                                            </div>
                                            <div className="cds-test-topic-right">
                                                <button
                                                  type="button"
                                                  className="cds-test-btn-outline"
                                                  disabled={!testAccess.allowed}
                                                  title={!testAccess.allowed ? testAccess.message : undefined}
                                                  onClick={() => handlePreSelectTema(tema, bloque.nombre)}
                                                >
                                                    {testAccess.allowed ? getMessage("tests.practice") : "Bloqueado"}
                                                </button>
                                            </div>
                                        </div>
                                        );
                                    })
                                ) : ( <p className="cds-test-empty-msg">No hay temas disponibles.</p> )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
        ) : null}

        {/* MODAL PRE-TEST */}
        {showPreTestModal && preTestInfo && (
            <div className="cds-test-modal-overlay fade-in" role="presentation">
                <div
                  className="cds-test-modal-box scale-in"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="cds-pretest-title"
                >
                    <button
                      type="button"
                      className="cds-test-modal-close"
                      onClick={isDirectEmbeddedLaunch ? leaveToCourse : closePreTestModal}
                      aria-label="Cerrar"
                      disabled={isStartingTest}
                    >
                      ✕
                    </button>
                    
                    <header className="cds-test-modal-header">
                        <Icon name="trophy" className="cds-test-modal-icon-top"/>
                        <h3 id="cds-pretest-title">{preTestInfo.temaObj.nombre}</h3>
                        <p>{preTestInfo.bloque}</p>
                    </header>
                    
                    <main className="cds-test-modal-body">
                        <div className="cds-test-stats-grid">
                            <div className="cds-test-stat-item">
                                <span>Aprobado</span>
                                <strong>{preTestInfo.temaObj.notaMinima.toFixed(1)}</strong>
                            </div>
                            <div className="cds-test-stat-item">
                                <span>Tiempo/Preg</span>
                                <strong>{preTestInfo.temaObj.tiempoPorPregunta}s</strong>
                            </div>
                        </div>

                        {startTestError ? (
                          <p className="cds-test-start-error" role="alert">
                            {startTestError}
                          </p>
                        ) : null}

                        <div className="cds-test-modal-start-actions">
                          {preTestInfo.intentos < MAX_INTENTOS ? (
                            <button
                              type="button"
                              className="cds-test-btn-primary full-width"
                              disabled={isStartingTest || !testAccess.allowed}
                              aria-busy={isStartingTest}
                              onClick={() => handleSelectTema(preTestInfo.temaObj, preTestInfo.bloque)}
                            >
                              {resolveStartEvaluationLabel(isStartingTest)}
                            </button>
                          ) : (
                            <button type="button" className="cds-test-btn-disabled full-width" disabled>
                              {getMessage("tests.limitReached")}
                            </button>
                          )}
                        </div>

                        <div className="cds-test-history-wrapper">
                            <h4>Historial ({preTestInfo.intentos}/{MAX_INTENTOS})</h4>
                            {!preTestInfo.historial || preTestInfo.historial.length === 0 ? (
                                <div className="cds-test-first-attempt">Primer intento. ¡A por todas!</div>
                            ) : (
                                <div className="cds-test-history-scroll">
                                    {preTestInfo.historial.slice(-5).reverse().map((h, i) => {
                                        const hasReview =
                                          Array.isArray(h.detalleRevision) &&
                                          filterFailedReviewRows(h.detalleRevision).length > 0;
                                        return (
                                        <button
                                          key={i}
                                          type="button"
                                          className={`cds-test-history-row ${h.estado === 'dominado' ? 'success' : 'fail'}${hasReview ? ' cds-test-history-row--reviewable' : ''}`}
                                          disabled={!hasReview || isStartingTest}
                                          onClick={() => hasReview && setHistoryReviewEntry(h)}
                                        >
                                            <span className="cds-test-h-date">{new Date(h.fecha || h.timestamp).toLocaleDateString()}</span>
                                            <span className="cds-test-h-score">
                                              {Number.isFinite(Number(h.nota)) ? Number(h.nota).toFixed(1) : "—"}
                                            </span>
                                            <span className="cds-test-h-badge">{h.estado === 'dominado' ? 'APTO' : 'NO APTO'}</span>
                                            {hasReview ? (
                                              <span className="cds-test-h-review-hint">Ver revisión</span>
                                            ) : null}
                                        </button>
                                    );
                                    })}
                                </div>
                            )}
                        </div>
                    </main>
                </div>
            </div>
        )}

        {historyReviewEntry && Array.isArray(historyReviewEntry.detalleRevision) ? (() => {
            const failedHistoryRows = filterFailedReviewRows(historyReviewEntry.detalleRevision);
            if (failedHistoryRows.length === 0) return null;
            return (
            <div className="cds-test-modal-overlay fade-in" role="presentation">
                <div className="cds-test-modal-box scale-in cds-test-review-modal" role="dialog" aria-labelledby="cds-history-review-title">
                    <button
                      type="button"
                      className="cds-test-modal-close"
                      onClick={() => setHistoryReviewEntry(null)}
                      aria-label="Cerrar revisión"
                    >
                      ✕
                    </button>
                    <header className="cds-test-modal-header">
                        <h3 id="cds-history-review-title">Revisión del intento</h3>
                        <p>
                          Nota: {Number.isFinite(Number(historyReviewEntry.nota))
                            ? Number(historyReviewEntry.nota).toFixed(1)
                            : "—"} / 10
                        </p>
                        <div
                          className="cds-test-review-jump-actions"
                          aria-label={"Navegaci\u00f3n de la revisi\u00f3n"}
                        >
                          <button type="button" onClick={() => scrollHistoryReview("start")}>
                            Primera pregunta
                          </button>
                          <button type="button" onClick={() => scrollHistoryReview("end")}>
                            {"\u00daltima pregunta"}
                          </button>
                        </div>
                    </header>
                    <main
                      ref={historyReviewBodyRef}
                      className="cds-test-modal-body cds-test-review-list"
                      role="region"
                      aria-label="Preguntas incorrectas del intento"
                      tabIndex={0}
                    >
                        {failedHistoryRows.map((row, idx) => (
                          <div
                            key={idx}
                            className={`cds-test-review-row ${row.esCorrecta ? "ok" : "fail"}`}
                          >
                            <div className="cds-test-review-q">
                              <strong>Pregunta {idx + 1}:</strong> {row.pregunta || "(vacía)"}
                            </div>
                            <div className="cds-test-review-a">
                              Tu respuesta: {row.respuesta || "(sin respuesta)"}
                            </div>
                            <div className="cds-test-review-status">
                              {row.esCorrecta ? "Correcta" : "Incorrecta o sin responder"}
                            </div>
                            {!row.esCorrecta && row.respuestaCorrecta ? (
                              <div className="cds-test-review-correct">
                                Respuesta correcta: {row.respuestaCorrecta}
                              </div>
                            ) : null}
                          </div>
                        ))}
                    </main>
                </div>
            </div>
            );
        })() : null}
      </div>
    );
  }

  const unansweredCount = () =>
    preguntasTest.filter((_, idx) => respuestasUsuario[idx] === undefined).length;

  // EJECUCIÓN DEL TEST
  if (view === "test") {
      const preguntaActual = preguntasTest[currentQuestionIndex];
      const respuestaSeleccionada = respuestasUsuario[currentQuestionIndex];
      const aciertos = testFinalizado ? resultAciertos : 0;
      const notaFinal = testFinalizado ? resultNotaFinal : 0;
      const passed = testFinalizado ? resultPassed : false;

      return (
          <div className="cds-test-root dark-theme cds-test-run-page test-running">
              <header className="cds-test-header cds-test-header--run">
                <h1>{cursoTitulo || activeCourse?.titulo || ""}</h1>
                <p>{getMessage("campus.courseContentSubtitle")}</p>
              </header>
              <div className={`cds-test-run-box fade-in${testFinalizado ? " is-results" : ""}`}>
                  <header className="cds-test-run-header">
                      <button
                        className="cds-test-btn-ghost"
                        onClick={handleRunExitClick}
                        aria-label="Salir del test"
                      >
                        ✕ Salir
                      </button>
                      <div className="cds-test-run-meta">
                          <h2>Pregunta {currentQuestionIndex + 1} / {preguntasTest.length}</h2>
                      </div>
                      <div className="cds-test-run-header-actions">
                        {shouldShowFinishTestButton(testFinalizado) ? (
                          <button
                            type="button"
                            className="cds-test-btn-finish cds-test-btn-finish--header"
                            onClick={handleRunFinishClick}
                          >
                            Terminar test
                          </button>
                        ) : null}
                        <div className="cds-test-timer">{formatTime(tiempoRestante)}</div>
                      </div>
                  </header>

                  <div className="cds-test-run-progress">
                      <div className="cds-test-run-fill" style={{width: `${((currentQuestionIndex + 1) / preguntasTest.length) * 100}%`}}></div>
                  </div>

                  {!testFinalizado ? (
                    <nav className="cds-test-question-nav" aria-label="Navegación por preguntas">
                      {preguntasTest.map((_, idx) => {
                        const answered = respuestasUsuario[idx] !== undefined;
                        const marked = preguntasMarcadasRevision[idx] === true;
                        const isCurrent = idx === currentQuestionIndex;
                        return (
                          <button
                            key={idx}
                            type="button"
                            className={`cds-test-q-pill${isCurrent ? " current" : ""}${answered ? " answered" : ""}${marked ? " marked" : ""}`}
                            onClick={() => setCurrentQuestionIndex(idx)}
                            aria-label={`Ir a pregunta ${idx + 1}${marked ? ", marcada para revisión" : ""}`}
                            aria-current={isCurrent ? "step" : undefined}
                          >
                            {idx + 1}
                          </button>
                        );
                      })}
                    </nav>
                  ) : null}

                  <main className="cds-test-run-body">
                      {!testFinalizado ? (
                          <div className="question-area">
                              <div className="cds-test-question-toolbar">
                                <button
                                  type="button"
                                  className={`cds-test-mark-review-btn${preguntasMarcadasRevision[currentQuestionIndex] ? " active" : ""}`}
                                  onClick={() => togglePreguntaMarcadaRevision(currentQuestionIndex)}
                                >
                                  {preguntasMarcadasRevision[currentQuestionIndex]
                                    ? "Quitar marca de revisión"
                                    : "Marcar para revisar"}
                                </button>
                              </div>
                              {preguntaActual.imagenUrl ? (
                                <img
                                  src={preguntaActual.imagenUrl}
                                  alt="Imagen de la pregunta"
                                  className="cds-test-q-image"
                                />
                              ) : null}
                              {preguntaActual.pregunta ? (
                                <h3 className="cds-test-q-text">{preguntaActual.pregunta}</h3>
                              ) : null}
                              <div className="cds-test-answers">
                                  {preguntaActual.respuestasMezcladas.map((resp, idx) => {
                                      const isSelected = respuestaSeleccionada === idx;
                                      return (
                                          <button key={idx} className={`cds-test-answer-btn ${isSelected ? 'selected' : ''}`} onClick={() => handleRespuesta(idx)}>
                                              <div className="cds-test-answer-letter">{String.fromCharCode(65 + idx)}</div>
                                              <span className="cds-test-answer-text">{resp.texto}</span>
                                          </button>
                                      );
                                  })}
                              </div>
                          </div>
                      ) : (
                          <div className="cds-test-results-view">
                              <div className={`cds-test-score-circle ${passed ? "pass" : "fail"}`}>
                                  <span className="cds-test-score-big">{notaFinal.toFixed(1)}</span>
                                  <span className="cds-test-score-small">/ 10</span>
                              </div>
                              <h2 className="cds-test-result-title">
                                  {passed ? "¡Test superado!" : "Test no superado"}
                              </h2>
                              <p className="cds-test-result-summary">
                                {aciertos} aciertos de {preguntasTest.length} preguntas
                              </p>
                              <div
                                className="cds-test-feedback-box"
                                role="note"
                                aria-live="polite"
                              >
                                <strong>{getMessage("tests.reviewOnceWarningTitle")}</strong>
                                <p>{getMessage("tests.reviewOnceWarningBody")}</p>
                              </div>
                              <div className="cds-test-review-list" role="region" aria-label="Revisión de preguntas">
                                {buildTestReviewRows(preguntasTest, respuestasUsuario).map((row) => {
                                  const p = preguntasTest[row.index];
                                  const marked = preguntasMarcadasRevision[row.index] === true;
                                  return (
                                    <div
                                      key={row.index}
                                      className={`cds-test-review-row ${row.isCorrect ? "ok" : "fail"}${marked ? " marked" : ""}`}
                                    >
                                      <div className="cds-test-review-q">
                                        <strong>Pregunta {row.index + 1}:</strong> {row.pregunta}
                                        {marked ? (
                                          <span className="cds-test-review-marked-badge">Marcada en el examen</span>
                                        ) : null}
                                      </div>
                                      <div className="cds-test-review-a">
                                        Tu respuesta: {row.answerText}
                                      </div>
                                      <div className="cds-test-review-status">
                                        {row.isCorrect ? "Correcta" : row.answerText === "(sin respuesta)" ? "Sin responder" : "Incorrecta"}
                                      </div>
                                      {!row.isCorrect ? (
                                        <div className="cds-test-review-correct">
                                          Respuesta correcta: {getCorrectAnswerText(p) || "(no disponible)"}
                                        </div>
                                      ) : null}
                                      {p?.explicacion ? (
                                        <div className="cds-test-review-explain">{p.explicacion}</div>
                                      ) : null}
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="cds-test-result-actions">
                                  <button className="cds-test-btn-outline" type="button" onClick={reiniciarTest}>Reintentar test</button>
                                  <button className="cds-test-btn-primary" type="button" onClick={requestLeaveTestResults}>Volver al curso</button>
                              </div>
                          </div>
                      )}
                  </main>

                  {!testFinalizado && (
                      <footer className="cds-test-run-footer">
                          <button className="cds-test-btn-secondary" disabled={currentQuestionIndex === 0} onClick={() => setCurrentQuestionIndex(prev => prev - 1)}>Anterior</button>
                          <div className="cds-test-run-footer-nav">
                            {currentQuestionIndex < preguntasTest.length - 1 ? (
                                <button className="cds-test-btn-primary" type="button" onClick={() => setCurrentQuestionIndex(prev => prev + 1)}>Siguiente</button>
                            ) : null}
                            <button
                              className="cds-test-btn-finish cds-test-btn-finish--footer"
                              type="button"
                              onClick={() => setShowFinishConfirm(true)}
                            >
                              Terminar test
                            </button>
                          </div>
                      </footer>
                  )}
              </div>

              {shouldShowExitConfirmModal(testFinalizado, showExitConfirm) ? (
                <div className="cds-test-modal-overlay fade-in" role="presentation">
                  <div className="cds-test-confirm-dialog scale-in" role="alertdialog" aria-labelledby="cds-exit-title">
                    <h3 id="cds-exit-title">¿Salir del test?</h3>
                    <p>Perderás el progreso de este intento si sales ahora.</p>
                    <div className="cds-test-confirm-actions">
                      <button type="button" className="cds-test-btn-outline" onClick={() => setShowExitConfirm(false)}>Seguir en el test</button>
                      <button
                        type="button"
                        className="cds-test-btn-primary"
                        onClick={() => {
                          cancelPendingAutoFinalize();
                          setShowExitConfirm(false);
                          testSaveStartedRef.current = false;
                          testSavedRef.current = false;
                          leaveEmbeddedOrSelection();
                        }}
                      >
                        Salir sin guardar
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              {shouldShowFinishConfirmModal(testFinalizado, showFinishConfirm) ? (
                <div className="cds-test-modal-overlay fade-in" role="presentation">
                  <div className="cds-test-confirm-dialog scale-in" role="alertdialog" aria-labelledby="cds-finish-title">
                    <h3 id="cds-finish-title">¿Terminar el test?</h3>
                    <p>
                      {unansweredCount() > 0
                        ? `Tienes ${unansweredCount()} pregunta(s) sin responder. Se guardará la nota actual.`
                        : "Se guardará tu resultado y podrás revisar cada respuesta."}
                    </p>
                    <div className="cds-test-confirm-actions">
                      <button type="button" className="cds-test-btn-outline" onClick={() => setShowFinishConfirm(false)}>Cancelar</button>
                      <button
                        type="button"
                        className="cds-test-btn-finish"
                        onClick={() => {
                          void finalizarTest();
                        }}
                      >
                        Confirmar y terminar
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              {showLeaveResultsConfirm && testFinalizado ? (
                <div className="cds-test-modal-overlay fade-in" role="presentation">
                  <div className="cds-test-confirm-dialog scale-in" role="alertdialog" aria-labelledby="cds-leave-results-title">
                    <h3 id="cds-leave-results-title">{getMessage("tests.leaveResultsTitle")}</h3>
                    <p>{getMessage("tests.leaveResultsBody")}</p>
                    <div className="cds-test-confirm-actions">
                      <button type="button" className="cds-test-btn-outline" onClick={() => setShowLeaveResultsConfirm(false)}>
                        Seguir revisando
                      </button>
                      <button
                        type="button"
                        className="cds-test-btn-primary"
                        onClick={() => {
                          setShowLeaveResultsConfirm(false);
                          leaveToCourse();
                        }}
                      >
                        Volver al curso
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
          </div>
      );
  }

  return null;
}

TestTemas.propTypes = {
  embeddedQuery: PropTypes.shape({
    pestana: PropTypes.string,
    bloque: PropTypes.string,
    tema: PropTypes.string,
    curso: PropTypes.string,
  }),
  onEmbeddedClose: PropTypes.func,
};

export default TestTemas;
